import React from 'react';
import { useMutation } from 'hooks/apollo';
import { Button, Col, Form, Input, Row } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'translations/messages';

import Page from 'components/Page';
import { EDIT_CUSTOMER } from 'shared/constants/permissions';
import ImageInput from 'components/ImageInput';
import FormLabel from 'components/FormLabel';
import useCustomer from 'hooks/useCustomer';
import { gql } from '@apollo/client';
import useGlobalContext from 'hooks/useGlobalContext';
import messages from './messages';

const MUTATION = gql`
  mutation CustomerSettings(
    $id: ID!,
    $input: UpdateCustomerInput!
  ) {
    updateCustomer(
      id: $id,
      input: $input,
    ) {
      id
      name
      alias
      logo { url }
    }
  }
`;

const CustomerSettings = ({ customer }) => {
  const [mutate, { loading }] = useMutation(MUTATION);
  const [form] = Form.useForm();
  const { message } = useGlobalContext();
  const { formatMessage: $t } = useIntl();
  const { hasCustomerPermission } = useCustomer();

  const onFinish = async (values) => {
    try {
      const { logo, ...input } = values;
      if (logo instanceof File) {
        input.logo = logo;
      }

      await mutate({ variables: { id: customer.id, input } });
      message.success($t(messages.saveSuccess));
    } catch {
      message.error($t(messages.saveError));
    }
  };

  const canEdit = hasCustomerPermission(EDIT_CUSTOMER);

  const initialValues = { ...customer, logo: customer.logo?.url };

  return (
    <Form layout="vertical" onFinish={onFinish} form={form} initialValues={initialValues}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label={<FormattedMessage {...globalMessages.name} />}
            rules={[{ required: true, message: $t(globalMessages.required) }]}
          >
            <Input disabled={!canEdit} />
          </Form.Item>
          <Form.Item
            name="alias"
            label={<FormLabel label={messages.aliasLabel} description={messages.aliasDescription} />}
          >
            <Input disabled={!canEdit} placeholder={customer.name} />
          </Form.Item>
        </Col>
        <Col offset={2} span={8}>
          <Form.Item
            name="logo"
            label={<FormLabel label={messages.logoLabel} description={messages.logoDescription} />}
            help={<FormattedMessage {...messages.logoHelp} />}
          >
            <ImageInput disabled={!canEdit} />
          </Form.Item>
        </Col>
      </Row>
      {canEdit && (
      <Page.Footer>
        <Button type="primary" htmlType="submit" loading={loading}>
          <FormattedMessage {...globalMessages.save} />
        </Button>
      </Page.Footer>
      )}
    </Form>
  );
};

export default CustomerSettings;
